/**
 * DexLiveScanMenu — Push 7 / v1.4.2 b3.
 *
 * Pokédex-only scan menu. Originally introduced in Push 6 to pivot the
 * Dex page off the rejected Push 5 wiring that re-attached the legacy
 * multi-screen Live Scan modal. Push 7 wires the previously-stubbed
 * Live Scan action into a real Dex-only live OCR + PiP flow without
 * touching the old LiveScanModal component or any of its surfaces.
 *
 * Actions:
 *   1. Live Scan         — starts the iOS broadcast extension, consumes
 *                          frames through the existing broadcast +
 *                          App-Group bridges, runs the Pokédex OCR per
 *                          frame, and ingests dex numbers from the
 *                          4×4 visible grid into dexStateStore. The
 *                          button changes to "Stop & save" while a
 *                          session is running. PiP shows Dex-specific
 *                          guidance (Open Pokédex / Hold steady /
 *                          Reading 4×4 grid / Dex updated).
 *   2. Start / Close PiP — toggles iOS Picture in Picture via the
 *                          existing nexPip bridge. Status / reason
 *                          comes back from the bridge unmodified.
 *   3. Upload screenshots — opens the existing ScanPokedexModal so the
 *                          import flow stays available alongside live.
 *
 * The diagnostic block is fed by `dexScanDiagnostics`, which now also
 * receives per-frame snapshots from the live scan loop. Source flips
 * between "Live scan" and "Screenshot upload" automatically.
 *
 * Strict non-features (still enforced from Push 6):
 *   - No box / detail / catch scan menus.
 *   - No CP OCR, IV display, instance IDs, nicknames.
 *   - No trusted scan summary panel.
 *   - No 12-slot box rows or row/slot diagnostics.
 *   - The legacy LiveScanModal is never mounted from this menu.
 */
import { hashNavigate } from "@/lib/hashRouter";
import { useEffect, useRef, useState } from "react";
import { armStopPicker, disarmStopPicker } from "@/lib/broadcastAutoStart";
import { getPlatform } from "@/lib/platform";
import {
  X, Radio, Image as ImageIcon, Layers, ClipboardCopy,
  AlertCircle, Activity, Sparkles, Square, Loader2, Check, Copy, HelpCircle,
} from "lucide-react";
import { copyToClipboard } from "@/lib/copyToClipboard";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { ScanPokedexModal } from "@/components/ScanPokedexModal";
import { DexScanGapReview } from "@/components/DexScanGapReview";
import { ScanSetupGuide } from "@/components/ScanSetupGuide";
import { ScanHudPreview } from "@/components/ScanHudPreview";
import { getDexScanSettings, setDexScanSettings } from "@/lib/dexScanSettings";
import {
  conflictPresentation,
  type DexReconciliationConflict,
} from "@/lib/dexReconciliation";
import { setManual } from "@/lib/dexStateStore";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import {
  DEX_SCAN_DIAG_EVENT,
  formatLastScanAgo,
  formatModeLabel,
  readDexScanDiagnostics,
  type DexScanDiagnostics,
  type DexScanGatedCounts,
} from "@/lib/dexScanDiagnostics";
import {
  isPipSupported,
  preparePip,
  startPip,
  stopPip,
  getPipDiagnostics,
  subscribePipState,
} from "@/lib/nexPip";
import { useLiveScan } from "@/components/LiveScanProvider";
import { useAuthOptional } from "@/components/AuthProvider";
import { armRatingMoment } from "@/lib/ratingDwell";
import { ratingLog } from "@/lib/ratingLog";
import { requestProfileSetup } from "@/lib/profileSetupRequest";
import { isTrainerProfileSet } from "@/lib/trainerSetup";

export interface DexLiveScanMenuProps {
  open: boolean;
  onClose: () => void;
  /** Variant the calling Pokédex page is showing — passed through to the upload modal. */
  initialVariant: PokedexVariant;
  /** Called after a screenshot upload confirms, so the caller can refresh KPIs. */
  onConfirm?: () => void;
  /**
   * Hide the "Upload screenshots" row (2026-08-28): My Pokémon moved uploads
   * to its own Upload menu, so its live-scan menu is live scan only. The
   * Pokédex page keeps the row — this menu is its only upload entry.
   */
  hideUpload?: boolean;
}

interface PipStatus {
  supported: boolean;
  possible: boolean;
  active: boolean;
  reason?: string;
}

function emptyPipStatus(): PipStatus {
  return { supported: false, possible: false, active: false };
}

export function DexLiveScanMenu({ open, onClose, initialVariant, onConfirm, hideUpload }: DexLiveScanMenuProps) {
  // OPTIONAL auth: this menu is reachable from surfaces that do not require an
  // AuthProvider in tests. No provider means no account, which means the gate
  // below stays out of the way rather than blocking a scan it cannot assess.
  const auth = useAuthOptional();
  const account = auth?.account ?? null;
  const profileComplete = account == null || isTrainerProfileSet(account);
  const { toast } = useToast();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [setupGuideOpen, setSetupGuideOpen] = useState(false);
  const [pip, setPip] = useState<PipStatus>(emptyPipStatus);
  const [diag, setDiag] = useState<DexScanDiagnostics | null>(() => readDexScanDiagnostics());
  // Copy-on-read toggle (My Pokémon scan only; see dexScanSettings).
  const [copyNames, setCopyNames] = useState(() => getDexScanSettings().copyNicknameOnScan);
  const [nowTick, setNowTick] = useState(0);
  const [diagOpen, setDiagOpen] = useState(false);
  // b17a — bidirectional reconciliation conflicts pending review. 3+ conflicts
  // mount the DexScanGapReview card; 1-2 are shown as toasts and not held here.
  const [gapConflicts, setGapConflicts] = useState<DexReconciliationConflict[]>([]);
  // PR AA: the rolling list of newly-added dex numbers is now tracked inside
  // the live scan hook and rendered by the native PiP HUD (2x5 grid), not by
  // this sheet. The menu no longer needs onNewAdds.
  //
  // 2026-07: the scan session is now the app-level LiveScanProvider singleton,
  // not a per-menu instance. This menu mounts only on /dex and unmounts on every
  // navigation away; instantiating the hook here meant leaving the page mid-scan
  // tore down the native broadcast and re-fired the iOS picker. Reading the
  // shared session keeps the scan alive across navigation. `initialVariant` is
  // still used below for labels + applyFallback; the provider tracks the pill
  // for the scan's own fallback.
  const liveScan = useLiveScan();

  // b17a — surface bidirectional reconciliation conflicts after a scan. The
  // engine has already gated these on confidence + the "Suggest gaps" setting,
  // so by the time they reach here every one is a high-confidence "you may not
  // have this yet" worth showing. Routing per the spec's Conflict UX:
  //   - 1-2 conflicts → an inline toast each, framed positively, with
  //     "Not caught" (apply) and "Keep as caught" (dismiss) actions. Confirm
  //     records a manual "not caught" mark the user owns; nothing is mutated
  //     until the user taps it.
  //   - 3+ conflicts → the DexScanGapReview summary card with a per-Pokémon
  //     confirm, so a stack of toasts doesn't bury the screen.
  // Returns true when it surfaced anything (so the caller knows a conflict UI
  // is now in front of the user).
  function surfaceConflicts(conflicts: DexReconciliationConflict[]): boolean {
    const presentation = conflictPresentation(conflicts.length);
    if (presentation === "none") return false;
    if (presentation === "summary") {
      setGapConflicts(conflicts);
      toast({
        title: `Nexus spotted ${conflicts.length} possible gaps`,
        description: "Tap to review entries that may not be caught yet.",
        duration: 10000,
      });
      return true;
    }
    // 1-2 conflicts: one toast each. TOAST_LIMIT=1 means the newest shows, but
    // each carries its own resolve action so neither is lost.
    for (const c of conflicts) {
      toast({
        title: "Nexus spotted a possible gap",
        description: `You don't seem to have ${c.speciesName} yet. Mark as not caught?`,
        duration: 12000,
        action: (
          <ToastAction
            altText={`Mark ${c.speciesName} as not caught`}
            onClick={() => {
              setManual(c.variant, c.dexNumber, "missing");
              onConfirm?.();
            }}
          >
            Not caught
          </ToastAction>
        ),
      });
    }
    return true;
  }

  // Diagnostic store subscription — re-render when a real scan publishes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => setDiag(readDexScanDiagnostics());
    window.addEventListener(DEX_SCAN_DIAG_EVENT, refresh);
    return () => window.removeEventListener(DEX_SCAN_DIAG_EVENT, refresh);
  }, []);

  // Tick the "Ns ago" label once a second while the menu is open.
  useEffect(() => {
    // The "Ns ago" label this ticks for lives in the DEV-only diagnostics
    // block, so production never pays a 1 Hz re-render of the sheet for it.
    if (!open || !import.meta.env.DEV) return;
    const id = window.setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // PiP support + state. We sample once on open then subscribe so the
  // toggle reflects whether the user dismissed PiP from the system UI.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        // Scan overhaul v2 trigger fix (2026-07-07): opening the menu must
        // NEVER touch the PiP / broadcast pipeline. This probe previously called
        // preparePip() — which constructs and ARMS the native AVKit PiP
        // controller — plus canAutoStartBroadcast() on EVERY menu open. Because
        // the menu is surfaced via requestDexLiveScan() from generic surfaces
        // (nav, resume banner, cross-route arrivals), that armed PiP the moment
        // any of those opened the sheet, which then boot-strapped the
        // semi-broken broadcast pipeline (device log 2026-07-07: "menu open —
        // pip probe" → startPip → NexPip3D.start()). Live scan — and with it PiP
        // preparation and the broadcast picker — now starts ONLY from the
        // explicit Live Scan action (handleLiveScan → liveScan.start(), which
        // runs autoStartPip()/preparePip()/startBroadcast() itself). Here we
        // read PiP SUPPORT and the CURRENT diagnostics PASSIVELY for the
        // readiness pill: no prepare, no broadcast probe, no start.
        // Both bridge probes in flight together (2026-09-22): serial awaits
        // were two round trips before the READY badge could settle.
        const [support, cur] = await Promise.all([isPipSupported(), getPipDiagnostics()]);
        if (cancelled) return;
        if (import.meta.env.DEV) {
          console.log("[live-scan] menu open, pip probe (passive)", {
            supported: support.supported,
            possible: support.possible,
            diagPossible: cur?.isPossible === true,
          });
        }
        setPip({
          supported: support.supported,
          possible: support.possible || cur?.isPossible === true,
          active: cur?.isActive === true,
          reason: support.reason,
        });
      } catch (err) {
        if (!cancelled) {
          setPip({ supported: false, possible: false, active: false, reason: String(err) });
        }
      }
    })();
    const unsub = subscribePipState((d) => {
      setPip((prev) => ({ ...prev, active: d.isActive === true }));
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [open]);

  // iOS 26.7: the system broadcast picker must receive the trainer's REAL
  // tap to present "Stop Broadcast" (a programmatic tap presents "Start" over
  // the live broadcast, device passes 2026-09-24). While a scan runs, the
  // native picker is laid invisibly over the Stop & save button, tracking
  // its on-screen rect; the tap opens the system's own Stop sheet and the
  // broadcast's `finished` notification closes the scan. Parked again when
  // the scan ends or the menu closes.
  const stopButtonRef = useRef<HTMLButtonElement | null>(null);
  const scanningNow = open && liveScan.state.phase === "scanning";
  useEffect(() => {
    if (getPlatform() !== "ios" || !scanningNow) return;
    let raf = 0;
    let last = "";
    const place = () => {
      const el = stopButtonRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const key = [r.x, r.y, r.width, r.height].map((v) => Math.round(v)).join(",");
      if (key !== last) {
        last = key;
        void armStopPicker({ x: r.x, y: r.y, width: r.width, height: r.height });
      }
    };
    const tick = () => {
      place();
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(raf);
      void disarmStopPicker();
    };
  }, [scanningNow]);

  // OUT OF THE WAY ONCE THE SCAN IS LIVE (2026-09-28 owner direction).
  // Trainers hop between GO and Nexus Dex mid-scan, and this sheet was still
  // up over the Pokédex / My Pokémon every time they came back, so they
  // closed it every time. The moment the phase turns "scanning" the sheet
  // dismisses itself; the floating Nexus Dex orb (LiveScanFloatingOrb) then
  // stands in for it and re-opens it on a tap. Keyed on the TRANSITION into
  // scanning, so re-opening the sheet from the orb (phase already scanning)
  // leaves it open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const prevPhaseRef = useRef(liveScan.state.phase);
  useEffect(() => {
    const phase = liveScan.state.phase;
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (open && phase === "scanning" && prev !== "scanning") onCloseRef.current();
  }, [liveScan.state.phase, open]);

  if (!open) return null;

  // Closing the menu mid-scan only hides it; the scan carries on. Until
  // 2026-09-28 a close on Android / web also tore the session down "so a
  // broadcast is never left running by dismissing the sheet"; now the
  // floating orb shows the running scan on every screen, and the sheet
  // closes ITSELF when the scan goes live, so a close must never be a stop.
  // Stopping is the red Stop & save button (on iOS, the armed picker).
  function handleClose() {
    onClose();
  }

  async function handlePipToggle() {
    try {
      if (pip.active) {
        const ok = await stopPip();
        toast({
          title: ok ? "HUD closed" : "HUD was not active",
        });
      } else {
        // The overlay permission is off by default on every fresh Android
        // install, and start() only ever REPORTS that it is missing, which left
        // this button a dead end: a red "overlay permission not granted" toast
        // and no way to fix it from here. prepare() is the method that opens
        // "Display over other apps".
        //
        // Ask start() FIRST and only fall back on its answer, rather than
        // gating on `pip.possible`. That flag is a snapshot taken when the
        // sheet opened, so it is stale for the whole return trip through the
        // settings screen — gating on it sent a user who had JUST granted the
        // permission back to settings, with a toast telling them to do the
        // thing they had already done. The native call is the only source that
        // is right at the moment of the tap.
        const res = await startPip();
        if (res.started) {
          toast({ title: "HUD started" });
        } else if (/overlay permission/i.test(res.reason ?? "")) {
          await preparePip().catch(() => null);
          toast({
            title: "Allow the HUD to draw over other apps",
            description:
              "Turn on the permission on the screen that just opened, then tap Start HUD again.",
          });
        } else {
          toast({
            title: "HUD could not start",
            description: res.reason ?? "Try again in the app.",
            variant: "destructive",
          });
        }
      }
    } catch (err: any) {
      toast({
        title: "HUD error",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    }
  }

  // Viral engine, phase 1: right after the FIRST successful scan — the "value
  // first" moment, the dex just visibly filled itself — offer the invite link
  // once, ever. A toast (not a modal) so it can't block the post-scan flow,
  // and deliberately NOT a recurring "added" toast (Z1 removed those); the
  // localStorage flag makes this a one-shot per device.
  // Returns true when the invite toast went up, so the rating ask below can
  // stand aside for it.
  function maybeOfferInviteShare(appliedCount: number): boolean {
    const FLAG = "nexusdex.scanInvitePromptShown";
    try {
      if (localStorage.getItem(FLAG)) return false;
      localStorage.setItem(FLAG, String(Date.now()));
    } catch {
      return false; // storage unavailable: skip rather than risk re-prompting forever
    }
    toast({
      title: `Scan complete: ${appliedCount} added`,
      description:
        "Want friends to see what you're hunting? Share your invite: when 2 friends join and finish a scan, you all get 7 days of Pro free.",
      duration: 12000,
      action: (
        <ToastAction
          altText="Share your Nexus Dex invite link"
          onClick={() => {
            void (async () => {
              const { shareInvite } = await import("@/lib/inviteShare");
              const outcome = await shareInvite().catch(() => "cancelled" as const);
              if (outcome === "copied") {
                toast({ title: "Invite copied", description: "Your invite link is on the clipboard." });
              }
            })();
          }}
        >
          Share
        </ToastAction>
      ),
    });
    return true;
  }

  async function handleLiveScan() {
    const phase = liveScan.state.phase;
    if (phase === "scanning") {
      // Finalise: stop the broadcast loop, publish a "saved" snapshot,
      // bubble the result back to the page so the grid re-renders.
      const { totals, pendingFallback, conflicts, myPokemon } = await liveScan.finish();
      // A session spent on individual caught-Pokémon pages writes to MY
      // POKÉMON, not the dex — "Nothing read yet" was a lie for exactly the
      // session that worked perfectly (2026-08-20 device report). Say where
      // the data went, and offer the page it went to.
      if ((totals?.applied ?? 0) === 0 && myPokemon.pokemonSeen > 0) {
        surfaceConflicts(conflicts);
        // A success: the store rating may follow, once the trainer has opened
        // My Pokémon and had a look at what landed (lib/ratingDwell).
        armRatingMoment("scan", "my-pokemon");
        toast({
          title: `${myPokemon.pokemonSeen} Pokémon saved to My Pokémon`,
          description: "CP, IVs and moves recorded from their pages.",
          duration: 8000,
          action: (
            <ToastAction
              altText="Open My Pokémon"
              onClick={() => hashNavigate("/my-pokemon")}
            >
              View
            </ToastAction>
          ),
        });
        onConfirm?.();
        return;
      }
      if (totals && totals.applied > 0) {
        // Z1 — the transient "added" toast is gone; the persistent rolling
        // "Added" list above is now the post-scan confirmation of what was
        // newly added, since the toast vanished before users could read it.
        // We still surface reconciliation conflicts inline.
        onConfirm?.();
        // A scan that added entries is a moment of success. The one-shot
        // invite toast goes first; otherwise the store rating may follow,
        // once the trainer has looked over the grid and paused
        // (lib/ratingDwell; lib/storeReview still decides whether to ask).
        if (maybeOfferInviteShare(totals.applied)) ratingLog("no moment at scan: the one-time invite toast took this success");
        else armRatingMoment("scan", "dex");
        return;
      }
      // Past here the scan added nothing, so there is no success to ask on.
      ratingLog("no moment at scan: nothing added to Nexus Dex or My Pokémon");
      // Build 3 / v1.4.6 b3 — the scan read real grid entries but never resolved
      // a confident PoGo variant, so nothing applied per-frame. Rather than the
      // old silent "no new entries", confirm a LAST-RESORT fallback to the
      // Nexus-side filter the user picked before scanning. Applying is the
      // user's choice (the toast action), never automatic mid-scan.
      if (pendingFallback && pendingFallback.count > 0) {
        const fallbackLabel = variantLabel(initialVariant);
        toast({
          title: `Detected ${pendingFallback.count} entr${pendingFallback.count === 1 ? "y" : "ies"}`,
          description: `Couldn't read the filter pill, apply to ${fallbackLabel}?`,
          duration: 12000,
          action: (
            <ToastAction
              altText={`Apply ${pendingFallback.count} entries to ${fallbackLabel}`}
              onClick={() => {
                void (async () => {
                  const res = await liveScan.applyFallback(initialVariant);
                  toast({
                    title: "Dex Live Scan saved",
                    description: res.applied > 0
                      ? `${res.applied} dex entr${res.applied === 1 ? "y" : "ies"} applied to ${fallbackLabel}.`
                      : "No new entries to apply.",
                  });
                  onConfirm?.();
                })();
              }}
            >
              Apply
            </ToastAction>
          ),
        });
        surfaceConflicts(conflicts);
        return;
      }
      // Nothing new applied. Distinguish the two very different reasons so an
      // ALREADY-COMPLETE dex doesn't look identical to a broken scan:
      //   - the scan READ the grid but every cell was already caught (a complete
      //     / up-to-date dex — it worked, there was just nothing to add), vs
      //   - it couldn't read anything (a genuine miss).
      // `numbersRead > 0` with `applied === 0` is the "already caught" case: the
      // OCR parsed dex numbers, they resolved to a variant, but each was already
      // present so the store skipped them. (Reported as "shiny/lucky/perfect not
      // scanning" — they were 100% complete, so the live view sat still.)
      if (!surfaceConflicts(conflicts)) {
        const readSomething = (totals?.numbersRead ?? 0) > 0;
        const scannedLabel = variantLabel(totals?.lastVariant ?? initialVariant);
        toast(
          readSomething
            ? {
                title: "Nothing new to add",
                description: `Every Pokémon this scan saw is already in your ${scannedLabel} dex. You're up to date here.`,
              }
            : {
                title: "Nothing read yet",
                description: "The session ended without reading any entries. Open your Pokédex or a Pokémon and scroll slowly so the scan can see it.",
              },
        );
      }
      onConfirm?.();
      return;
    }
    if (phase === "starting" || phase === "finishing") return;
    if (phase === "saved" || phase === "error") {
      // Restart from a terminal phase: tear down first, then re-enter. A
      // broadcast still live is kept for start() to adopt, never re-tapped.
      await liveScan.stop({ keepBroadcast: true });
    }
    await liveScan.start("menu");
    if (liveScan.state.phase === "error") {
      toast({
        title: "Live Scan unavailable",
        description: liveScan.state.error ?? "Try again in the iOS app.",
        variant: "destructive",
      });
    }
  }

  function liveScanCopy(): { label: string; status: string; bubble: string } {
    switch (liveScan.state.phase) {
      case "starting":
        return { label: "Starting…", status: "Starting", bubble: "var(--livescan-warn)" };
      case "scanning":
        return { label: "Stop & save", status: "Scanning", bubble: "var(--livescan-badge)" };
      case "finishing":
        return { label: "Saving…", status: "Saving", bubble: "var(--livescan-warn)" };
      case "saved":
        return { label: "Live Scan", status: "Saved", bubble: "var(--livescan-badge)" };
      case "error":
        return { label: "Live Scan", status: "Error", bubble: "var(--livescan-danger)" };
      default:
        return { label: "Live Scan", status: "Ready", bubble: "var(--livescan-badge)" };
    }
  }

  const ago = formatLastScanAgo(Date.now());
  // nowTick is used purely to invalidate the "Ns ago" string each second.
  void nowTick;

  // A TRAINER NAME IS REQUIRED TO SCAN, and only to scan. Sign-up asks for it
  // (two typed fields, name and level, since 2026-10-02) and can be put off
  // with "Not now"; an account from before that may never have been asked. So
  // this is the backstop: a scan WRITES to a dex, and a dex belongs to a
  // trainer.
  //
  // "Add my trainer name" opens the step straight away (ProfileGate) instead
  // of leaving them on a dead end that explains why they cannot proceed.
  if (!profileComplete) {
    return (
      <div
        className="fixed inset-0 z-[75] flex items-end md:items-center justify-center bg-black/80"
        role="dialog"
        aria-label="Trainer name required"
        onClick={() => { void handleClose(); }}
        data-testid="dex-live-scan-profile-gate"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full md:max-w-md v2-neon-card p-5 rounded-t-3xl md:rounded-3xl"
          style={{ background: "var(--surface-dark-7)", border: "1px solid var(--accent-fill-glow)" }}
        >
          <p
            className="text-[10px] uppercase tracking-[0.22em] font-bold"
            style={{ color: "var(--livescan-eyebrow)" }}
          >
            ONE THING FIRST
          </p>
          <h3 className="text-lg font-extrabold mt-1" style={{ color: "var(--livescan-title)" }}>
            Add your trainer name
          </h3>
          <p className="text-[12px] mt-2 leading-snug" style={{ color: "var(--livescan-text-muted)" }}>
            A scan fills in your Pokédex, so it needs a trainer to belong to. Type your Pokémon GO
            trainer name and level, and every scan lands somewhere.
          </p>
          <div className="flex flex-col gap-2 mt-4">
            <button
              onClick={() => {
                requestProfileSetup(account?.id);
                void handleClose();
              }}
              data-testid="dex-live-scan-setup-profile"
              className="v2-neon-cta h-11 rounded-full font-semibold text-sm"
            >
              Add my trainer name
            </button>
            <button
              onClick={() => { void handleClose(); }}
              data-testid="dex-live-scan-gate-back"
              className="h-11 rounded-full text-sm font-semibold"
              style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--livescan-text-muted)" }}
            >
              Keep looking around
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[75] flex items-end md:items-center justify-center bg-black/80"
      role="dialog"
      aria-label="Dex Live Scan"
      onClick={() => { void handleClose(); }}
      data-testid="dex-live-scan-menu"
    >
      {/* The sheet sits at the bottom; everything above it was dimmed
          backdrop doing nothing (2026-08-31 owner ask). It now carries the
          picture of the HUD parked where THIS scan wants it — the Pokédex
          grid shot with its number-chip legend on the Dex menu, the
          own-Pokémon shot with the IV-bars-clear note on My Pokémon's. Same
          component the "How to scan" guide uses, so the two cannot drift.
          Tapping it opens the full guide at the matching page rather than
          dismissing, which is what a picture of the instructions should do. */}
      <div className="w-full md:max-w-md flex flex-col justify-end h-full pointer-events-none">
        <div
          className="min-h-0 flex items-end justify-center px-4 pb-3 shrink"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setSetupGuideOpen(true)}
            data-testid="dex-live-scan-hud-preview"
            aria-label="How to scan: see the full guide"
            className="pointer-events-auto block w-full max-w-[15rem] rounded-xl overflow-hidden"
          >
            <ScanHudPreview
              kind={hideUpload ? "pokemon" : "live"}
              testId="dex-live-scan-preview"
              imgClassName="object-cover"
            />
          </button>
        </div>
      <div
        onClick={(e) => e.stopPropagation()}
        className="pointer-events-auto w-full v2-neon-card p-5 max-h-[90dvh] overflow-y-auto rounded-t-3xl md:rounded-3xl"
        style={{ background: "var(--surface-dark-7)", border: "1px solid var(--accent-fill-glow)" }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.22em] font-bold"
              style={{ color: "var(--livescan-eyebrow)" }}
            >
              LIVE SCAN
            </p>
            <h3
              className="text-lg font-extrabold mt-1"
              style={{ color: "var(--livescan-title)" }}
              data-testid="dex-live-scan-title"
            >
              Nexus Dex Scan
            </h3>
            <p className="text-[11px] mt-1" style={{ color: "var(--livescan-text-muted)" }}>
              Scan your Pokémon GO Pokédex and/or Pokémon to update your Nexus Dex.
            </p>
            <button
              onClick={() => setSetupGuideOpen(true)}
              data-testid="dex-live-scan-howto"
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold rounded-full px-2.5 py-1"
              style={{
                background: "var(--accent-fill-glow-faint)",
                border: "1px solid var(--accent-fill-glow)",
                color: "var(--livescan-badge)",
              }}
            >
              <HelpCircle className="w-3.5 h-3.5" />
              How to scan
            </button>
          </div>
          <button
            onClick={() => { void handleClose(); }}
            aria-label="Close"
            data-testid="dex-live-scan-close"
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{
              background: "var(--surface-dark-9)",
              border: "1px solid var(--accent-fill-glow)",
              color: "var(--livescan-text)",
            }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Primary actions — exactly three, per user direction. */}
        <div className="flex flex-col gap-2.5">
          {(() => {
            const copy = liveScanCopy();
            const phase = liveScan.state.phase;
            const busy = phase === "starting" || phase === "finishing";
            const scanning = phase === "scanning";
            const Icon = scanning ? Square : (phase === "saved" ? Check : Radio);
            return (
              <button
                ref={stopButtonRef}
                onClick={handleLiveScan}
                disabled={busy}
                data-testid="dex-live-scan-live"
                className="w-full rounded-2xl py-3 inline-flex items-center justify-between gap-3 px-4 disabled:opacity-60"
                style={{
                  background: scanning
                    ? "hsl(0 78% 56% / 0.18)"
                    : "var(--accent-fill-glow-faint)",
                  border: scanning
                    ? "1px solid hsl(0 78% 56% / 0.55)"
                    : "1px solid var(--accent-fill-glow)",
                  color: scanning ? "var(--livescan-danger)" : "var(--livescan-text)",
                  minHeight: 56,
                }}
              >
                <span className="inline-flex items-center gap-2 text-sm font-bold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                  {copy.label}
                </span>
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                  style={{
                    background: scanning
                      ? "hsl(0 78% 56% / 0.18)"
                      : "hsl(43 96% 60% / 0.12)",
                    color: copy.bubble,
                    border: scanning
                      ? "1px solid hsl(0 78% 56% / 0.55)"
                      : "1px solid var(--accent-fill-glow)",
                  }}
                  data-testid="dex-live-scan-live-status"
                >
                  {copy.status}
                </span>
              </button>
            );
          })()}
          {liveScan.state.phase === "error" && liveScan.state.error && (
            <p
              className="text-[10px] -mt-1.5"
              style={{ color: "var(--livescan-danger)" }}
              data-testid="dex-live-scan-live-error"
            >
              {liveScan.state.error}
            </p>
          )}
          {/* Honest PiP status: if auto-start failed the scan still runs,
              but we say so rather than implying PiP is floating. */}
          {(liveScan.state.phase === "scanning" || liveScan.state.phase === "starting") &&
            liveScan.state.pipError && (
            <p
              className="text-[10px] -mt-1.5"
              style={{ color: "var(--livescan-warn)" }}
              data-testid="dex-live-scan-pip-warning"
            >
              HUD didn’t auto-start ({liveScan.state.pipError}). Live scan is
              still running. Tap Start HUD to retry the floating window.
            </p>
          )}
          {/* b18g's "Scroll back to #XYZ" nudge used to render here. Removed: the
              PiP HUD already carries live scroll guidance (direction arrow +
              "Looking for: #N"), which is where the user is actually looking —
              Pokemon GO is in focus during a scan, not this sheet. In the sheet
              the pill only ever appeared after the fact, overflowing the row
              next to "How to scan". */}

          {/* PR AA: the rolling "Added:" row that used to live here has moved
              to the Picture-in-Picture overlay (a 2x5 dex-number grid under
              the scanning + added lines), so the user can watch OCR progress
              while Pokemon GO is in focus. The adds are tracked in the live
              scan hook and pushed to the native HUD via setDexPipPhase. */}

          <button
            onClick={handlePipToggle}
            disabled={!pip.supported}
            data-testid="dex-live-scan-pip"
            className="w-full rounded-2xl py-3 inline-flex items-center justify-between gap-3 px-4 disabled:opacity-50"
            style={{
              background: "var(--surface-dark-9)",
              border: pip.active
                ? "1px solid var(--accent-fill-glow-strong)"
                : "1px solid var(--accent-fill-glow)",
              color: "var(--livescan-text)",
              minHeight: 56,
            }}
          >
            <span className="inline-flex items-center gap-2 text-sm font-bold">
              {pip.active ? <Square className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
              {pip.active ? "Close HUD" : "Start HUD"}
            </span>
            <span
              className="text-[10px] uppercase tracking-wider font-bold"
              style={{ color: pip.supported ? "var(--livescan-badge)" : "var(--livescan-text-muted)" }}
              data-testid="dex-live-scan-pip-status"
            >
              {pip.supported
                ? pip.active
                  ? "Active"
                  : pip.possible
                  ? "Ready"
                  : "Not ready"
                : "Unsupported"}
            </span>
          </button>

          {hideUpload && (
            <button
              type="button"
              role="switch"
              aria-checked={copyNames}
              onClick={() => {
                const next = !copyNames;
                setCopyNames(next);
                setDexScanSettings({ copyNicknameOnScan: next });
              }}
              data-testid="dex-live-scan-copy-names"
              className="w-full rounded-2xl py-3 inline-flex items-center justify-between gap-3 px-4"
              style={{
                background: "var(--surface-dark-9)",
                border: "1px solid var(--accent-fill-glow-subtle)",
                color: "var(--livescan-text)",
                minHeight: 56,
              }}
            >
              <span className="inline-flex flex-col items-start text-left">
                <span className="inline-flex items-center gap-2 text-sm font-bold">
                  <ClipboardCopy className="w-4 h-4" />
                  Copy names as they're read
                </span>
                <span className="text-[10px] mt-0.5" style={{ color: "var(--text-tertiary)" }}>
                  Each Pokémon's name in your template, once, ready to paste.
                </span>
              </span>
              <span
                className="text-[10px] uppercase tracking-wider font-bold"
                style={{ color: copyNames ? "var(--livescan-badge)" : "var(--text-tertiary)" }}
              >
                {copyNames ? "On" : "Off"}
              </span>
            </button>
          )}

          {!hideUpload && (
          <button
            onClick={() => setUploadOpen(true)}
            data-testid="dex-live-scan-upload"
            className="w-full rounded-2xl py-3 inline-flex items-center justify-between gap-3 px-4"
            style={{
              background: "var(--surface-dark-9)",
              border: "1px solid var(--accent-fill-glow)",
              color: "var(--livescan-text)",
              minHeight: 56,
            }}
          >
            <span className="inline-flex items-center gap-2 text-sm font-bold">
              <ImageIcon className="w-4 h-4" />
              Upload screenshots
            </span>
            <span
              className="text-[10px] uppercase tracking-wider font-bold"
              style={{ color: "var(--livescan-badge)" }}
            >
              Ready
            </span>
          </button>
          )}
        </div>

        {/* Optional reason / hint line for unsupported PiP. */}
        {!pip.supported && pip.reason && (
          <p
            className="text-[10px] mt-2"
            style={{ color: "var(--livescan-text-muted)" }}
            data-testid="dex-live-scan-pip-reason"
          >
            {pip.reason}
          </p>
        )}

        {/* Diagnostic toggle. The Dex diagnostic menu is a separate
            section so testers can see scan state without it cluttering
            the primary flow.
            PR-F (v1.4.6): gated behind `import.meta.env.DEV` so the whole
            diagnostic surface is compile-time stripped from production builds
            (prod renders no diagnostics, no toggle). */}
        {import.meta.env.DEV && (
        <div className="mt-5">
          <button
            onClick={() => setDiagOpen((v) => !v)}
            data-testid="dex-live-scan-diag-toggle"
            className="w-full rounded-2xl py-2.5 inline-flex items-center justify-between gap-2 px-3"
            style={{
              background: "var(--surface-dark-9)",
              border: "1px solid var(--accent-fill-glow-subtle)",
              color: "var(--text-ink-bright-faint)",
            }}
          >
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold">
              <Activity className="w-3.5 h-3.5" />
              Dex diagnostic menu
            </span>
            <span className="text-[10px]" style={{ color: "var(--text-ink-muted)" }}>
              {diagOpen ? "Hide" : "Show"} · {diag ? (ago ?? "now") : "no scan"}
            </span>
          </button>

          {diagOpen && (
            <div
              className="mt-2 rounded-2xl p-3"
              style={{
                background: "var(--surface-dark-9)",
                border: "1px dashed var(--accent-fill-glow)",
              }}
              data-testid="dex-live-scan-diag-panel"
            >
              {/* Push 8: live lifecycle status straight off the hook so
                  testers can confirm broadcast + PiP teardown. */}
              <div
                className="flex items-center justify-between mb-2 pb-2"
                style={{ borderBottom: "1px dashed var(--accent-fill-glow-subtle)" }}
              >
                <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-ink-muted)" }}>
                  Broadcast
                </span>
                <span
                  className="text-[11px] font-bold tabular-nums"
                  style={{ color: "var(--text-ink-bright-soft)" }}
                  data-testid="dex-live-scan-diag-broadcast"
                >
                  {liveScan.state.broadcastPhase}
                </span>
              </div>
              <div
                className="flex items-center justify-between mb-2 pb-2"
                style={{ borderBottom: "1px dashed var(--accent-fill-glow-subtle)" }}
              >
                <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-ink-muted)" }}>
                  HUD
                </span>
                <span
                  className="text-[11px] font-bold"
                  style={{ color: "var(--text-ink-bright-soft)" }}
                  data-testid="dex-live-scan-diag-pip"
                >
                  {liveScan.state.pipActive
                    ? "active"
                    : liveScan.state.pipError
                    ? `failed: ${liveScan.state.pipError}`
                    : "inactive"}
                </span>
              </div>
              <DexScanDiagnosticPanel diag={diag} ago={ago} />
            </div>
          )}
        </div>
        )}
      </div>

      </div>

      <ScanSetupGuide
        open={setupGuideOpen}
        onClose={() => setSetupGuideOpen(false)}
        // The My Pokémon menu (hideUpload) teaches the individual-Pokémon
        // scan first; the Dex menu opens on the Pokédex grid page.
        initialPage={hideUpload ? "pokemon" : "live"}
      />

      <ScanPokedexModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onConfirm={() => {
          onConfirm?.();
        }}
        initialVariant={initialVariant}
      />

      <DexScanGapReview
        open={gapConflicts.length > 0}
        conflicts={gapConflicts}
        onClose={(confirmed) => {
          setGapConflicts([]);
          if (confirmed > 0) {
            onConfirm?.();
            toast({
              title: "Dex updated",
              description: `${confirmed} entr${confirmed === 1 ? "y" : "ies"} marked not caught.`,
            });
          }
        }}
      />
    </div>
  );
}

/* ─────────── Diagnostic panel ─────────── */

/**
 * Render the gated-reason counts as one compact line (e.g.
 * "3 overlay, 2 unstable"). Returns null when nothing was gated so the
 * row is omitted entirely on a clean run.
 */
function formatGatedSummary(gated: DexScanGatedCounts | undefined): string | null {
  if (!gated) return null;
  const parts: string[] = [];
  if (gated.overlay_frame) parts.push(`${gated.overlay_frame} overlay`);
  if (gated.not_pokedex_grid) parts.push(`${gated.not_pokedex_grid} not-grid`);
  if (gated.low_confidence) parts.push(`${gated.low_confidence} low-conf`);
  if (gated.unstable_number) parts.push(`${gated.unstable_number} unstable`);
  if (gated.manual_override) parts.push(`${gated.manual_override} manual`);
  return parts.length ? parts.join(", ") : null;
}

function DexScanDiagnosticPanel({
  diag, ago,
}: { diag: DexScanDiagnostics | null; ago: string | null }) {
  // "Copy JSON" feedback: idle → ok | fail, auto-resets after a moment.
  const [copyState, setCopyState] = useState<"idle" | "ok" | "fail">("idle");

  async function handleCopyJson(raw: string) {
    const ok = await copyToClipboard(prettyJson(raw));
    setCopyState(ok ? "ok" : "fail");
    setTimeout(() => setCopyState("idle"), 1800);
  }

  if (!diag) {
    return (
      <div className="text-center py-3" data-testid="dex-live-scan-diag-empty">
        <AlertCircle
          className="w-4 h-4 mx-auto mb-1"
          style={{ color: "var(--text-ink-faint)" }}
        />
        <p className="text-[11px]" style={{ color: "var(--text-ink-faint)" }}>
          No Dex scan yet.
        </p>
        <p className="text-[10px] mt-1" style={{ color: "var(--text-ink-faint)" }}>
          Upload a screenshot or wait for a Live Scan to populate this panel.
        </p>
      </div>
    );
  }

  // PR-C Problem 1: prefer the two-signal vote's human-readable provenance
  // ("Lucky (tab-OCR ✓ tint ✓)" / "Main (sticky)" / "unresolved") so the user
  // sees WHY a variant was chosen. Fall back to the bare variant + (inferred)
  // tag for snapshots that predate the vote.
  const variantValue = diag.variantDetail
    ? `${diag.variantDetail}${
        diag.variantConfidence != null
          ? ` · ${Math.round(diag.variantConfidence * 100)}%`
          : ""
      }`
    : diag.variant
    ? `${diag.variant}${diag.variantInferred ? " (inferred)" : ""}`
    : "-";

  const items: Array<[string, string]> = [
    ["Source", diag.source === "live" ? "Live scan" : "Screenshot upload"],
    ["Variant", variantValue],
    ["Region", diag.region],
    ["Mode", formatModeLabel(diag.mode, diag.regionReason)],
    ["Confidence", `${diag.confidence}%`],
  ];
  if (diag.framesApplied != null && diag.framesObserved != null) {
    items.push(["Frames analyzed", `${diag.framesApplied}/${diag.framesObserved} grid`]);
  }
  items.push(
    ["Candidate cells", `${diag.cellsObserved} (≤16/frame)`],
    ["Numbers read", String(diag.numbersRead)],
    ["Applied to Dex", String(diag.applied)],
    ["Skipped (manual)", String(diag.skippedManual)],
    ["Cells w/o number", String(diag.unknownCells)],
    ["Last frame", ago ?? "just now"],
  );
  if (diag.regionReason) items.push(["Region source", diag.regionReason]);
  if (diag.pipObscuredCells != null && diag.pipObscuredCells > 0) {
    items.push(["HUD-obscured cells", String(diag.pipObscuredCells)]);
  }

  // Push 1 / v1.4.3: a single compact "Ignored frames" row summarising why
  // frames/numbers were gated out. Only shown when something was rejected,
  // so the panel stays quiet on a clean run.
  const ignoredSummary = formatGatedSummary(diag.gated);
  if (ignoredSummary) items.push(["Ignored frames", ignoredSummary]);

  // Push 3 / v1.4.3 b3: advanced image-pipeline rows (registration + crops).
  const ip = diag.imagePipeline;
  if (ip) {
    items.push(["Registered", `${ip.newRegistrations}/${ip.entriesScanned}`]);
    items.push(["Crops saved", `${ip.cropsCaptured}${ip.cropsUnavailable ? ` (+${ip.cropsUnavailable} unavailable)` : ""}`]);
    if (ip.motionSkippedFrames) items.push(["Motion-skipped", String(ip.motionSkippedFrames)]);
    if (ip.dedupedSkipped) items.push(["Deduped", String(ip.dedupedSkipped)]);
  }

  return (
    <div data-testid="dex-live-scan-diag-rows">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3 h-3" style={{ color: "var(--accent-fill)" }} />
        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--accent-fill)" }}>
          Last scan
        </p>
      </div>
      <dl className="grid grid-cols-2 gap-y-1 gap-x-3">
        {items.map(([label, value]) => (
          <div key={label} className="contents">
            <dt
              className="text-[10px] uppercase tracking-wider"
              style={{ color: "var(--text-ink-muted)" }}
            >
              {label}
            </dt>
            <dd
              className="text-[11px] tabular-nums text-right"
              style={{ color: "var(--text-ink-bright-soft)" }}
              data-testid={`dex-live-scan-diag-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
      {diag.note && (
        <p className="text-[10px] mt-2" style={{ color: "hsl(43 96% 80%)" }}>
          Note: {diag.note}
        </p>
      )}
      {diag.imagePipeline?.sessionJson && (
        <details className="mt-2" data-testid="dex-live-scan-diag-json">
          <summary
            className="text-[10px] uppercase tracking-wider cursor-pointer flex items-center gap-2"
            style={{ color: "var(--accent-fill)" }}
          >
            <span>Session JSON</span>
            <button
              type="button"
              data-testid="dex-live-scan-diag-json-copy"
              onClick={(e) => {
                // Don't toggle the <details> open/closed when copying.
                e.preventDefault();
                e.stopPropagation();
                void handleCopyJson(diag.imagePipeline!.sessionJson!);
              }}
              className="inline-flex items-center gap-1 text-[9px] font-bold normal-case tracking-normal rounded-full px-2 py-0.5"
              style={{
                background: "var(--surface-dark-10)",
                border: "1px solid var(--accent-fill-glow)",
                color:
                  copyState === "fail"
                    ? "hsl(0 80% 78%)"
                    : "var(--text-ink-bright-faint)",
              }}
              aria-label="Copy session JSON to clipboard"
            >
              {copyState === "ok" ? (
                <><Check className="w-3 h-3" /> Copied</>
              ) : copyState === "fail" ? (
                <><Copy className="w-3 h-3" /> Copy failed</>
              ) : (
                <><Copy className="w-3 h-3" /> Copy JSON</>
              )}
            </button>
          </summary>
          <pre
            className="text-[9px] mt-1 p-2 rounded-lg overflow-x-auto whitespace-pre-wrap break-all"
            style={{
              background: "var(--surface-dark-6)",
              border: "1px solid var(--accent-fill-glow-subtle)",
              color: "var(--text-ink-bright-faint)",
              maxHeight: 200,
            }}
          >
            {prettyJson(diag.imagePipeline.sessionJson)}
          </pre>
        </details>
      )}
    </div>
  );
}

/**
 * Build 3 / v1.4.6 b3 — human label for a variant id, used in the end-of-
 * session fallback confirmation ("apply to Lucky?"). "pokemon" is the
 * unfiltered main dex, shown as "Pokémon".
 */
function variantLabel(variant: PokedexVariant): string {
  if (variant === "pokemon") return "Pokémon";
  if (variant === "xxs" || variant === "xxl" || variant === "gmax") {
    return variant.toUpperCase();
  }
  return variant.charAt(0).toUpperCase() + variant.slice(1);
}

/** Pretty-print the session JSON; fall back to the raw string on parse error. */
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
